/**
 * cacheInterceptor.js — Axios request/response/error interceptors
 *
 * Mirrors the Flutter CacheInterceptor with:
 *  - L1 (in-memory Map) for instant hits within the session
 *  - L2 (IndexedDB via feedCache.js) for persistent disk cache
 *  - Stale-while-revalidate on network errors
 *  - Cross-resource invalidation on mutations
 */

import {
  putCache,
  getCache,
  getStaleCache,
  invalidateCache,
  shouldCache,
  makeCacheKey,
  getTTL,
} from './feedCache';

// ─── L1 In-Memory Cache ───────────────────────────────────
const memoryCache = new Map();
const MEMORY_TTL = 60 * 1000; // 1 minute

function memGet(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiry) {
    memoryCache.delete(key);
    return null;
  }
  return entry.data;
}

function memSet(key, data) {
  memoryCache.set(key, {
    data,
    expiry: Date.now() + MEMORY_TTL,
  });
}

// ─── Cross-Invalidation Map ──────────────────────────────
const INVALIDATION_MAP = {
  'update_friend_request': ['my_profile', 'friend_list', 'contact_suggestion'],
  'friend_request': ['my_profile', 'friend_list', 'contact_suggestion'],
  'profileUpdate': ['my_profile'],
  'post_likes': ['get_post_sync'],
  'post_unlike': ['get_post_sync'],
  'post_comments': ['get_post_comments'],
  'comment_reply': ['get_post_comments'],
  'comment_delete': ['get_post_comments'],
  'save_friend_post': ['get_save_post'],
};

/**
 * Install cache interceptors on an Axios instance.
 *
 * @param {import('axios').AxiosInstance} axiosInstance
 */
export function installCacheInterceptors(axiosInstance) {
  // ─── Request Interceptor ────────────────────────────────
  axiosInstance.interceptors.request.use(async (config) => {
    // Only cache GET requests
    if (config.method?.toLowerCase() !== 'get') {
      // On mutations (POST/PUT/DELETE), invalidate related caches
      const url = config.url || '';
      for (const [trigger, paths] of Object.entries(INVALIDATION_MAP)) {
        if (url.includes(trigger)) {
          // Invalidate L1
          for (const key of memoryCache.keys()) {
            if (paths.some((p) => key.includes(p))) {
              memoryCache.delete(key);
            }
          }
          // Invalidate L2
          await invalidateCache(paths);
          break;
        }
      }
      return config;
    }

    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    if (!shouldCache(fullUrl)) return config;

    const cacheKey = makeCacheKey('GET', fullUrl);

    // L1 check
    const memHit = memGet(cacheKey);
    if (memHit) {
      // Return cached data by setting an adapter that resolves immediately
      config._cacheHit = true;
      config._cachedData = memHit;
      config.adapter = () =>
        Promise.resolve({
          data: memHit,
          status: 200,
          statusText: 'OK (L1 Cache)',
          headers: {},
          config,
        });
      return config;
    }

    // L2 check (IndexedDB)
    const diskHit = await getCache(cacheKey);
    if (diskHit) {
      memSet(cacheKey, diskHit); // Promote to L1
      config._cacheHit = true;
      config._cachedData = diskHit;
      config.adapter = () =>
        Promise.resolve({
          data: diskHit,
          status: 200,
          statusText: 'OK (L2 Cache)',
          headers: {},
          config,
        });
      return config;
    }

    // Store the cache key for the response interceptor
    config._cacheKey = cacheKey;
    return config;
  });

  // ─── Response Interceptor ───────────────────────────────
  axiosInstance.interceptors.response.use(async (response) => {
    // Skip if this was a cache hit
    if (response.config._cacheHit) return response;

    const cacheKey = response.config._cacheKey;
    if (!cacheKey) return response;

    // Only cache successful responses
    if (response.status >= 200 && response.status < 300) {
      const ttl = getTTL(cacheKey);
      memSet(cacheKey, response.data);
      await putCache(cacheKey, response.data, ttl);
    }

    return response;
  });

  // ─── Error Interceptor (Stale-While-Revalidate) ─────────
  axiosInstance.interceptors.response.use(undefined, async (error) => {
    const config = error.config;
    if (!config || config.method?.toLowerCase() !== 'get') {
      return Promise.reject(error);
    }

    const fullUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url;
    if (!shouldCache(fullUrl)) return Promise.reject(error);

    const cacheKey = makeCacheKey('GET', fullUrl);

    // Try stale data from IndexedDB
    const stale = await getStaleCache(cacheKey);
    if (stale) {
      console.warn(
        `[CacheInterceptor] Serving stale data for ${config.url} ` +
        `(age: ${Math.round(stale.age / 1000)}s)`
      );
      return {
        data: stale.data,
        status: 200,
        statusText: 'OK (Stale Cache)',
        headers: { 'x-cache-stale': 'true', 'x-cache-age': stale.age },
        config,
      };
    }

    return Promise.reject(error);
  });
}

/**
 * Clear all caches (L1 + L2). Useful for logout.
 */
export async function clearAllCaches() {
  memoryCache.clear();
  const { clearAllCache } = await import('./feedCache');
  await clearAllCache();
}
